import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Loading from "../common/Loading";
import MusicShell from "../fanHome/MusicShell";
import { US_STATES } from "../common/BillingAddressFields";
import { API_BASE_URL, ACCENT, getAuthToken, Field, PrimaryButton, inputClass, ringStyle } from "../artistStudio/shared";
import { fieldErrors, claimErrorText, formatDay } from "./rightsClaimApi";

// /rights/privacy-request (mobile screen 43): a signed-in person in a US state sends a privacy rights request. We reply within 45 days.
// Below the form: the requests they already sent, with reference, reply-by date and status.
export const REQUEST_TYPES = [
  { value: "access", label: "Know / access my data", note: "A copy of the personal data we hold about me." },
  { value: "delete", label: "Delete my data", note: "Delete my personal data (some records we must keep by law)." },
  { value: "correct", label: "Correct my data", note: "Fix something that is wrong." },
  { value: "opt_out", label: "Opt out of sale or sharing", note: "Stop my data being sold or shared for advertising." },
];
const STATUS = { received: "Received", in_review: "In review", completed: "Completed", declined: "Declined" };
const url = (path) => `${API_BASE_URL}/v1/privacy-request${path}`;
const config = () => ({ headers: { token: getAuthToken(), "Content-Type": "application/json" }, timeout: 20000 });

export default function PrivacyRights() {
  const [form, setForm] = useState({ state: "", request_type: "", details: "", contact_email: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(null);
  const [list, setList] = useState({ loading: true, items: [] });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(url("/mine"), config());
      setList({ loading: false, items: r.data?.result?.items || [] });
    } catch (e) {
      setList({ loading: false, items: [] });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const local = {};
    if (!form.state) local.state = "Choose your state.";
    if (!form.request_type) local.request_type = "Choose what you want to ask for.";
    if (form.contact_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email.trim())) local.contact_email = "Enter a valid email address.";
    setErrors(local);
    setMessage("");
    setSent(null);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      const body = { state: form.state, request_type: form.request_type };
      if (form.details.trim()) body.details = form.details.trim();
      if (form.contact_email.trim()) body.contact_email = form.contact_email.trim();
      const r = await axios.post(url(""), body, config());
      setSent(r.data?.result);
      setForm({ state: "", request_type: "", details: "", contact_email: "" });
      load();
    } catch (e) {
      setErrors(fieldErrors(e));
      setMessage(claimErrorText(e, "Your request could not be sent. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <MusicShell maxWidth="max-w-2xl">
      <Link to="/rights" className="text-sm font-semibold hover:underline" style={{ color: ACCENT }}>← Rights &amp; legal</Link>
      <h1 className="mt-3 text-2xl md:text-3xl font-bold text-gray-900">State privacy rights</h1>
      <p className="mt-1 text-gray-600">If you live in a US state with a privacy law, you can ask to see, delete or correct your personal data, or opt out of its sale or sharing. We reply within 45 days.</p>
      {sent && (
        <div className="mt-5 rounded-2xl border border-gray-200 bg-[#f4f7f4] p-4" role="status" data-testid="privacy-sent">
          <p className="font-semibold text-gray-900">Request received: {sent.reference}</p>
          <p className="mt-1 text-sm text-gray-700">We will reply by {formatDay(sent.respond_by)}. We may contact you to confirm it is really you.</p>
        </div>
      )}
      <form onSubmit={submit} noValidate className="mt-6 space-y-4 rounded-2xl border border-gray-200 bg-white p-5" data-testid="privacy-form">
        <Field label="Your state" required error={errors.state}>
          <select value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} className={inputClass(!!errors.state)} style={ringStyle} aria-label="Your state">
            <option value="">Choose a state</option>
            {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </Field>
        <Field label="What do you want to ask for?" required error={errors.request_type}>
          <div role="radiogroup" aria-label="Request type" className="space-y-2">
            {REQUEST_TYPES.map((t) => (
              <label key={t.value} className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-3 text-sm">
                <input type="radio" name="request_type" value={t.value} checked={form.request_type === t.value} onChange={() => setForm((f) => ({ ...f, request_type: t.value }))} className="mt-1" />
                <span><span className="block font-semibold text-gray-900">{t.label}</span><span className="text-gray-500">{t.note}</span></span>
              </label>
            ))}
          </div>
        </Field>
        <Field label="Details (optional)" error={errors.details}>
          <textarea value={form.details} onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))} rows={4} maxLength={2000} className={inputClass(!!errors.details)} style={ringStyle} aria-label="Details" />
        </Field>
        <Field label="Email to reply to (optional, defaults to your account email)" error={errors.contact_email}>
          <input type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} maxLength={255} className={inputClass(!!errors.contact_email)} style={ringStyle} aria-label="Email to reply to" />
        </Field>
        {message && <p className="text-sm text-red-600" role="alert">{message}</p>}
        <PrimaryButton type="submit" disabled={busy} className="w-full !py-3">{busy ? "Sending…" : "Send request"}</PrimaryButton>
      </form>

      <h2 className="mt-8 text-lg font-bold" style={{ color: ACCENT }}>Your requests</h2>
      {list.loading ? <div className="flex justify-center py-8" role="status" aria-label="Loading requests"><Loading /></div>
        : list.items.length === 0 ? <p className="mt-2 text-sm text-gray-500">No requests yet.</p>
          : (
            <ul className="mt-3 space-y-3" data-testid="privacy-list">
              {list.items.map((r) => (
                <li key={r.id} className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
                  <p className="font-semibold text-gray-900">{r.type_label} <span className="font-normal text-gray-500">· {r.reference}</span></p>
                  <p className="mt-0.5 text-sm text-gray-600">{STATUS[r.status] || r.status} · sent {formatDay(r.received_at)} · reply by {formatDay(r.respond_by)}</p>
                </li>
              ))}
            </ul>
          )}
    </MusicShell>
  );
}
